"use client"

import { create } from 'zustand'
import {
  createArea,
  deleteArea,
  listAreas,
  reorderAreas,
  subscribeToAreas,
  updateArea,
  type Area,
  type AreaId,
  type AreaScope,
} from '@/lib/convex-areas'

/**
 * The user's areas, held once for the whole app.
 *
 * Areas are read almost everywhere — both task modals, the planner banner,
 * insights, settings, onboarding — so this subscribes **once** and shares the
 * result, rather than each surface opening its own watch.
 *
 * The live subscription is the single source of truth: mutations here do not
 * write optimistically, they just await the server and let the watch push the
 * new list. Areas are tiny and change rarely, so the round trip is invisible
 * and it removes any chance of the local list drifting from the server's
 * `position` ordering.
 */
interface AreasState {
  areas: Area[]
  isLoading: boolean
  error: string | null

  initialize: () => () => void
  createArea: (input: { name: string; color?: string; icon?: string; scope?: AreaScope; weeklyTargetMinutes?: number }) => Promise<Area>
  updateArea: (
    id: AreaId,
    input: {
      name?: string
      color?: string
      icon?: string
      scope?: AreaScope
      weeklyTargetMinutes?: number | null
      archived?: boolean
    },
  ) => Promise<Area>
  reorderAreas: (orderedIds: AreaId[]) => Promise<void>
  deleteArea: (id: AreaId) => Promise<void>
}

/** Guards against a second subscription when two components initialize at once. */
let activeUnsubscribe: (() => void) | null = null
let subscriberCount = 0

export const useAreasStore = create<AreasState>()((set) => ({
  areas: [],
  isLoading: true,
  error: null,

  initialize: () => {
    subscriberCount += 1

    if (!activeUnsubscribe) {
      activeUnsubscribe = subscribeToAreas((areas) => {
        set({ areas, isLoading: false, error: null })
      })

      // The watch only fires on change; this first read is what resolves the
      // loading state for a user who has no areas yet.
      void listAreas()
        .then((areas) => set({ areas, isLoading: false }))
        .catch((error: unknown) => {
          console.error('[areas] initial load failed:', error)
          set({ isLoading: false, error: 'Could not load your areas' })
        })
    }

    return () => {
      subscriberCount -= 1
      if (subscriberCount <= 0) {
        activeUnsubscribe?.()
        activeUnsubscribe = null
        subscriberCount = 0
      }
    }
  },

  createArea: async (input) => {
    const area = await createArea(input)
    set((state) =>
      state.areas.some((existing) => existing.id === area.id)
        ? state
        : { areas: [...state.areas, area].sort((a, b) => a.position - b.position) },
    )
    return area
  },

  updateArea: async (id, input) => {
    const area = await updateArea(id, input)
    set((state) => ({
      areas: state.areas.map((existing) => (existing.id === id ? area : existing)),
    }))
    return area
  },

  reorderAreas: async (orderedIds) => {
    // Reordering is a drag, so this one *is* optimistic — waiting for a round
    // trip before the row moves would make the drag feel broken.
    set((state) => ({
      areas: [...state.areas].sort(
        (a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id),
      ),
    }))
    await reorderAreas(orderedIds)
  },

  deleteArea: async (id) => {
    await deleteArea(id)
    set((state) => ({ areas: state.areas.filter((area) => area.id !== id) }))
  },
}))
